/**
 * Y/N price pill — rounded button with optional fee-adjusted American odds.
 * Honours the dashboard-only Fees/Raw toggle (see memory
 * feedback_fees_toggle_scope.md): toggle on → odds via effectivePriceForOdds.
 */

import { cn } from '@/lib/infra/utils';
import { useDisplaySettings } from '@/stores/displaySettings';
import { useFeeFor } from '@/services/hydraFees';
import { americanOdds } from '../tradeModalBook';
import { effectivePriceForOdds } from './helpers';
import type { MarketSide, PriceMode } from '../types';

export interface PricePillProps {
  /** Display label inside the pill — typically 'Y'/'N', 'O'/'U'. */
  label: string;
  /** Raw cents; null renders an em-dash. */
  cents: number | null;
  /** Which side of the binary market this pill represents. */
  side: MarketSide;
  /** The toolbar's currently-active side — drives the highlight. */
  activeMarketSide: MarketSide;
  /**
   * The toolbar's currently-active price mode. Drives the fee
   * perspective applied to the displayed American odds:
   *   - 'maker' → pill shows the best bid (LIMIT-rest perspective) →
   *     maker fee rate
   *   - 'taker' → pill shows the best ask (crossing perspective) →
   *     taker fee rate
   * Without this prop the previous behaviour was to always apply the
   * maker rate — which under-quoted the fee in Taker mode on series
   * with maker-fee-bearing schedules, and silently did nothing on
   * quadratic series (maker rate = 0). See odds audit 2026-05-25.
   */
  priceMode: PriceMode;
  disabled: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Used to look up the per-series fee schedule when the dashboard's Fees
   * toggle is on. Polymarket / unknown tickers fall back to raw.
   */
  ticker?: string | null;
  /**
   * Maker-fee rebate fraction (0..1) to fold into maker-perspective odds.
   * Computed by the parent (org enrolled + MM Rebate toggle + live/pregame).
   * 0 = no rebate. Only applied when the pill's fee perspective is maker.
   */
  makerRebatePct?: number;
}

export function PricePill({
  label,
  cents,
  side,
  activeMarketSide,
  priceMode,
  disabled,
  onClick,
  ticker,
  makerRebatePct = 0,
}: PricePillProps) {
  const isActive = side === activeMarketSide;
  const sideClass =
    side === 'yes'
      ? isActive
        ? 'border-positive text-foreground bg-positive/10'
        : 'border-border/60 text-muted-foreground hover:text-positive hover:border-positive/60'
      : isActive
        ? 'border-negative text-foreground bg-negative/10'
        : 'border-border/60 text-muted-foreground hover:text-negative hover:border-negative/60';

  const feeAdjusted = useDisplaySettings((s) => s.feeAdjustedOdds);
  const feeEntry = useFeeFor(ticker);
  // Taker mode pills show the ask (crossing price) so the fee burden
  // is the taker rate; Maker mode pills show the bid (resting price)
  // so the fee burden is the maker rate. Without this branch the pill
  // odds under-quote the fee in Taker mode.
  // 'market' mode displays the last trade price (a crossing event), so its
  // fee perspective is the taker rate — same as 'taker'. The maker/taker
  // narrowing came in from main alongside dan's 'market'-mode restore.
  const feePerspective = priceMode === 'maker' ? 'maker' : 'taker';
  const effectiveCents =
    cents == null
      ? null
      : effectivePriceForOdds(cents, feeAdjusted, feeEntry, feePerspective, makerRebatePct);
  const odds = effectiveCents != null ? americanOdds(effectiveCents) : null;

  // Always render every slot — even when cents/odds are null — and reserve
  // consistent widths so a pill missing data lines up vertically with sibling
  // pills above and below it. `min-w-[Nch]` reserves N character widths via
  // the monospace font; `tabular-nums` keeps digit columns identical. Without
  // this, a `—` pill collapses narrower than its `52¢ | -135` neighbour and
  // the Y/N pair shifts off the column's vertical centre.
  return (
    <button
      type="button"
      disabled={disabled || cents == null}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[13px] leading-none transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
        sideClass,
      )}
      title={
        cents != null
          ? `Click to trade ${label} @ ${cents}¢${odds ? ` → ${odds}` : ''}${ticker ? `\n${ticker}` : ''}`
          : ticker ?? undefined
      }
    >
      <span className="font-semibold">{label}</span>
      {/* Price is the headline of the pill — render it a notch larger and
          heavier than the label/odds so the number reads first. */}
      <span className="text-right text-[16px] font-bold tabular-nums">
        {cents != null ? `${cents}¢` : '—'}
      </span>
      <span className="h-3 w-px self-center bg-current opacity-30" />
      <span className="min-w-[5ch] text-right tabular-nums opacity-70">
        {odds ?? '—'}
      </span>
    </button>
  );
}
